import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer } from "recharts";
import { useCSATTemporalEvolution, PeriodType } from '@/hooks/useCSATTemporalEvolution';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { statusColors } from '@/theme/colors';

interface TemporalEvolutionChartProps {
  daysRange: number;
}

export const TemporalEvolutionChart = ({ daysRange }: TemporalEvolutionChartProps) => {
  const [periodType, setPeriodType] = useState<PeriodType>('day');
  const { data, isLoading } = useCSATTemporalEvolution(daysRange, periodType);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Evolução Temporal da Satisfação</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Evolução Temporal da Satisfação</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            <p>Nenhum dado disponível</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartConfig = {
    averageRating: {
      label: "Média de Avaliação",
      color: statusColors.success,
    },
  };

  const periodLabel = periodType === 'day' ? 'Diário' : periodType === 'week' ? 'Semanal' : 'Mensal';

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Evolução Temporal da Satisfação</CardTitle>
          <div className="flex items-center gap-2">
            <Label htmlFor="period-type" className="text-sm text-muted-foreground whitespace-nowrap">
              Período:
            </Label>
            <Select
              value={periodType}
              onValueChange={(value) => setPeriodType(value as PeriodType)}
            >
              <SelectTrigger id="period-type" className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Diário</SelectItem>
                <SelectItem value="week">Semanal</SelectItem>
                <SelectItem value="month">Mensal</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="w-full min-h-[300px]">
          <ChartContainer config={chartConfig} className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: periodType === 'day' ? 50 : 30 }}>
                <XAxis
                  dataKey="period"
                  tick={{ fontSize: 12 }}
                  angle={periodType === 'day' ? -45 : 0}
                  textAnchor={periodType === 'day' ? 'end' : 'middle'}
                  height={periodType === 'day' ? 60 : 30}
                />
                <YAxis
                  domain={[0, 5]}
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => value.toFixed(1)}
                />
                <ChartTooltip
                  content={({ active, payload }) => {
                    if (!active || !payload || !payload[0]) return null;
                    
                    const data = payload[0].payload;
                    return (
                      <div className="bg-white p-3 rounded-lg shadow-lg border">
                        <div className="font-semibold mb-1">{data.period}</div>
                        <div className="text-sm">
                          <span className="text-muted-foreground">Média: </span>
                          <span className="font-semibold">{data.averageRating.toFixed(1)}/5.0</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {data.totalResponses} {data.totalResponses === 1 ? 'resposta' : 'respostas'}
                        </div>
                      </div>
                    );
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="averageRating"
                  stroke={statusColors.success}
                  strokeWidth={2}
                  dot={{ fill: statusColors.success, r: 4 }}
                  activeDot={{ r: 6, fill: statusColors.success }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        </div>
        <div className="mt-2 text-xs text-muted-foreground text-center">
          Visualização {periodLabel.toLowerCase()} • {data.length} período(s) com dados
        </div>
      </CardContent>
    </Card>
  );
};

